import { useState } from 'react';
import { ImageIcon, Loader2 } from 'lucide-react';
import { PortalDsEmptyState } from 'src/components/portal-design-system';
import { portalDs } from 'src/components/portal-design-system';
import { useGetUserJourneySessionQuery } from '../endpoints/user-journey.endpoints';
import {
  formatDuration,
  inferEventCategory,
  normaliseSessionEventChips,
} from '../utils/user-journey.utils';
import type { UserJourneySession } from '../types/user-journey.types';
import { EventChip } from './event-chip';

interface SessionCardProps {
  userId: string;
  session: UserJourneySession;
  expanded: boolean;
  onToggle: () => void;
}

function formatSessionDateTime(startedAt: string): string {
  const date = new Date(startedAt);
  const dateLabel = new Intl.DateTimeFormat('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date);
  const timeLabel = new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
  return `${dateLabel} · ${timeLabel}`;
}

function SessionDetail({
  userId,
  sessionIndex,
}: {
  userId: string;
  sessionIndex: number;
}) {
  const { data, isLoading, isError } = useGetUserJourneySessionQuery(
    { userId, sessionIndex },
    { skip: false },
  );

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 px-5 py-4 text-[13px] text-[#5E6278]">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        Loading session events…
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="px-5 py-4 text-[13px] text-[#DC2626]">
        Unable to load session events.
      </div>
    );
  }

  return (
    <ol className="divide-y divide-[#EDEEF2] border-t border-[#EDEEF2]">
      {data.events.map((event) => {
        const category = inferEventCategory(event.event_type, event.category);
        const timeLabel = new Intl.DateTimeFormat('en-GB', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        }).format(new Date(event.occurred_at));

        return (
          <li key={event.id} className="flex gap-4 px-5 py-4">
            <div className="w-20 shrink-0 font-mono text-[12px] text-[#9CA3AF]">
              {timeLabel}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <EventChip
                  chip={{
                    eventType: event.event_type,
                    count: 1,
                    category,
                  }}
                />
                {event.outcome ? (
                  <span className="text-[12px] text-[#5E6278]">{event.outcome}</span>
                ) : null}
              </div>
            </div>
            <div className="h-14 w-14 shrink-0 overflow-hidden rounded-lg border border-[#EDEEF2] bg-[#F7F8FA]">
              {event.thumbnail_url ? (
                <img
                  src={event.thumbnail_url}
                  alt=""
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-[#9CA3AF]">
                  <ImageIcon className="h-5 w-5" aria-hidden />
                </div>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

export function SessionCard({ userId, session, expanded, onToggle }: SessionCardProps) {
  const chips = normaliseSessionEventChips(session.events);

  return (
    <article className="overflow-hidden rounded-xl border border-[#EDEEF2] bg-white shadow-[0_1px_2px_rgba(13,15,26,0.05)]">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="w-full px-5 py-4 text-left transition-colors hover:bg-[#F7F8FA]"
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-[14px] font-semibold text-[#0D0F1A]">
              {formatSessionDateTime(session.started_at)}
            </p>
            <p className="mt-1 text-[12px] text-[#5E6278]">
              {formatDuration(session.duration_seconds)} · {session.event_count} events
            </p>
          </div>
          {session.is_abandoned ? (
            <span className="rounded-full border border-[#FDE68A] bg-[#FFFBEB] px-2.5 py-1 text-[11px] font-semibold text-[#D97706]">
              Abandoned
            </span>
          ) : null}
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {chips.map((chip, index) => (
            <EventChip key={`${chip.eventType}-${index}`} chip={chip} />
          ))}
        </div>

        <div className="mt-4 border-t border-[#EDEEF2] pt-3">
          <p className={portalDs.bodySm}>{session.summary}</p>
        </div>
      </button>

      {expanded ? (
        <SessionDetail userId={userId} sessionIndex={session.session_index} />
      ) : null}
    </article>
  );
}

interface SessionListProps {
  userId: string;
  sessions: UserJourneySession[];
}

export function SessionList({ userId, sessions }: SessionListProps) {
  const [expandedSessionIndex, setExpandedSessionIndex] = useState<number | null>(null);

  if (sessions.length === 0) {
    return (
      <PortalDsEmptyState
        title="No sessions yet"
        description="This user has no recorded activity events."
      />
    );
  }

  return (
    <div className="space-y-3">
      {sessions.map((session) => (
        <SessionCard
          key={session.session_index}
          userId={userId}
          session={session}
          expanded={expandedSessionIndex === session.session_index}
          onToggle={() => {
            setExpandedSessionIndex((current) =>
              current === session.session_index ? null : session.session_index,
            );
          }}
        />
      ))}
    </div>
  );
}
